// A small Markdown-to-HTML formatter for NuSI replies: paragraphs, headings,
// bullet and numbered lists, tables, **bold**, *italic*, `code` and links.
// Everything is HTML-escaped first, so a reply can't inject markup; only the
// tags made here come out (and Angular sanitizes [innerHTML] on top).

const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function inline(s: string): string {
  return escape(s)
    .replace(/`([^`]+)`/g, '<code class="px-1 rounded bg-gray-100 text-[12px]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener" class="text-amber-700 underline">$1</a>',
    );
}

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

export function renderNuaiMarkdown(text: string): string {
  const lines = String(text ?? '').replace(/\r/g, '').split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    // Table: a header row, a |---| row, then body rows.
    if (/^\s*\|.*\|\s*$/.test(line) && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) body.push(cells(lines[i++]));
      out.push(
        '<div class="overflow-x-auto my-2"><table class="text-xs border-collapse w-full">' +
          '<thead><tr>' + head.map((h) => `<th class="text-left font-semibold border-b border-gray-200 px-2 py-1">${inline(h)}</th>`).join('') + '</tr></thead>' +
          '<tbody>' + body.map((r) => '<tr>' + r.map((c) => `<td class="border-b border-gray-100 px-2 py-1 align-top">${inline(c)}</td>`).join('') + '</tr>').join('') + '</tbody>' +
          '</table></div>',
      );
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      out.push(`<p class="font-bold text-gray-900 mt-2">${inline(heading[2])}</p>`);
      i++;
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*•]\s+/, ''));
      out.push('<ul class="list-disc pl-5 my-1 space-y-0.5">' + items.map((t) => `<li>${inline(t)}</li>`).join('') + '</ul>');
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*\d+[.)]\s+/, ''));
      out.push('<ol class="list-decimal pl-5 my-1 space-y-0.5">' + items.map((t) => `<li>${inline(t)}</li>`).join('') + '</ol>');
      continue;
    }
    // Paragraph: this line plus following plain lines, kept as line breaks.
    // Always takes at least this line, so a stray "| ... |" can't stall the loop.
    const para: string[] = [inline(lines[i++])];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i]) &&
      !/^#{1,4}\s/.test(lines[i]) &&
      !/^\s*\|.*\|\s*$/.test(lines[i])
    ) {
      para.push(inline(lines[i++]));
    }
    out.push(`<p class="my-1">${para.join('<br>')}</p>`);
  }
  return out.join('');
}
